import { Search } from "lucide-react";
import type { CSSProperties } from "react";

import { cn } from "@/lib/utils";
import { useT } from "@/lib/i18n-dict";
import type { ScreenDef, ScreenTone } from "@/content/screens";

const TONE: Record<ScreenTone, string> = {
  ok: "border-primary/50 bg-primary/15 text-primary",
  warn: "border-amber-500/50 bg-amber-500/15 text-amber-600 dark:text-amber-300",
  late: "border-destructive/50 bg-destructive/15 text-destructive",
  muted: "border-border bg-surface text-muted-foreground",
};

const BAR_TONE: Record<ScreenTone, string> = {
  ok: "bg-primary/70 border-primary",
  warn: "bg-amber-500/60 border-amber-500",
  late: "bg-destructive/50 border-destructive",
  muted: "bg-foreground/15 border-border",
};

/**
 * A product screen drawn as isometric artwork instead of a screenshot.
 * Text comes from the translated screen definition, so every locale gets its
 * own "interface" without shipping localized images.
 */
export function IsoScreen({
  screen,
  className,
  caption,
}: {
  screen: ScreenDef;
  className?: string;
  /** Render the caption under the artwork (hero uses it, inline rows may not). */
  caption?: boolean;
}) {
  const t = useT();
  // Last column usually holds a status chip whose translated label is long —
  // give it extra width so it never gets squeezed.
  const middleCount = Math.max(screen.columns.length - 2, 0);
  const columnTemplate =
    screen.columns.length > 1
      ? `1.2fr repeat(${middleCount}, 1fr) 1.5fr`
      : "1.2fr";



  return (
    <figure className={cn("iso-screen m-0", className)} role="img" aria-label={t(screen.alt)}>
      <div className="iso-screen-frame lg:mx-2" aria-hidden>
        <div className="overflow-hidden rounded-xl border border-border bg-card shadow-iso-flat">
          {/* window chrome */}
          <div className="flex items-center gap-2 border-b border-border/70 bg-surface px-4 py-3">
            <span className="flex gap-1.5">
              <span className="size-2.5 rounded-full bg-foreground/20" />
              <span className="size-2.5 rounded-full bg-foreground/20" />
              <span className="size-2.5 rounded-full bg-primary/70" />
            </span>
            <span className="ms-2 truncate font-display text-sm font-semibold">
              {t(screen.title)}
            </span>
          </div>

          {/* tabs + action */}
          <div className="flex flex-wrap items-center gap-2 border-b border-border/70 px-4 py-3">
            {screen.tabs.map((tab, index) => (
              <span
                key={tab}
                className={cn(
                  "rounded-full border px-3 py-1 text-xs font-medium",
                  index === 0
                    ? "border-primary/50 bg-primary/15 text-primary"
                    : "border-border text-muted-foreground",
                )}
              >
                {t(tab)}
              </span>
            ))}
            <span className="flex w-full items-center justify-end gap-2 sm:ms-auto sm:w-auto">
              <span className="hidden items-center gap-1.5 rounded-full border border-border px-3 py-1 text-xs text-muted-foreground sm:flex">
                <Search className="size-3" />
                {t("Search")}
              </span>
              <span className="rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground">
                {t(screen.action)}
              </span>
            </span>
          </div>

          <div className={cn("grid gap-0", screen.side ? "lg:grid-cols-[1.5fr_1fr]" : "")}>
            {/* table */}
            <div className="min-w-0 px-4 py-3">
              <div
                className="hidden items-end gap-2 border-b border-border/70 pb-2 text-[0.68rem] font-semibold tracking-tight text-muted-foreground uppercase sm:grid sm:tracking-wide"
                style={{ gridTemplateColumns: columnTemplate }}

              >
                {screen.columns.map((column) => (
                  <span
                    key={column}
                    className="hyphens-auto leading-[1.15] break-words [overflow-wrap:anywhere]"
                  >
                    {t(column)}
                  </span>
                ))}
              </div>


              {screen.rows.map((row) => (
                <div
                  key={row.cells.join("-")}
                  className="grid grid-cols-2 items-start gap-x-4 gap-y-3 border-b border-border/50 py-3 text-xs last:border-0 sm:[grid-template-columns:var(--screen-columns)] sm:items-center sm:gap-2 sm:py-2.5"
                  style={{
                    "--screen-columns": columnTemplate,
                  } as CSSProperties}
                >
                  {row.cells.map((cell, cellIndex) => (
                    <span
                      key={cell + String(cellIndex)}
                      className={cn(
                        "min-w-0 leading-snug hyphens-auto break-words [overflow-wrap:anywhere]",
                        cellIndex === 0 ? "font-semibold text-foreground" : "text-foreground/80",
                      )}

                    >
                      <span className="mb-1 block text-[0.62rem] font-semibold text-muted-foreground uppercase sm:hidden">
                        {t(screen.columns[cellIndex] ?? "")}
                      </span>
                      {t(cell)}
                    </span>
                  ))}
                  {row.chip ? (
                    <span className="min-w-0">
                      <span className="mb-1 block text-[0.62rem] font-semibold text-muted-foreground uppercase sm:hidden">
                        {t(screen.columns[screen.columns.length - 1] ?? "")}
                      </span>
                      <span
                        className={cn(
                          "inline-block max-w-full rounded-full border px-2 py-0.5 text-[0.68rem] leading-snug font-medium break-words",
                          TONE[row.chip.tone],
                        )}
                      >
                        {t(row.chip.label)}
                      </span>
                    </span>
                  ) : null}
                  {typeof row.progress === "number" ? (
                    <span className="min-w-0">
                      <span className="mb-1 block text-[0.62rem] font-semibold text-muted-foreground uppercase sm:hidden">
                        {t(screen.columns[screen.columns.length - 1] ?? "")}
                      </span>
                      <span className="block h-1.5 w-full rounded-full bg-foreground/10">
                        <span
                          className="block h-full rounded-full bg-primary"
                          style={{ width: `${row.progress}%` }}
                        />
                      </span>
                    </span>
                  ) : null}
                </div>
              ))}

              {screen.lanes?.length ? (
                <div className="mt-4 space-y-2.5">
                  {screen.lanes.map((lane) => (
                    <div key={lane.lane} className="grid grid-cols-[92px_1fr] items-center gap-3">
                      <span className="truncate text-[0.7rem] text-muted-foreground">
                        {t(lane.lane)}
                      </span>
                      <span className="relative block h-6 rounded-md border border-border/60 bg-surface">
                        {lane.bars.map((bar) => (
                          <span
                            key={bar.label}
                            className={cn(
                              "absolute top-1 bottom-1 flex items-center justify-center rounded-sm border px-1 text-[0.6rem] font-semibold text-foreground",
                              BAR_TONE[bar.tone ?? "muted"],
                            )}
                            style={{ insetInlineStart: `${bar.start}%`, width: `${bar.span}%` }}
                          >
                            <span className="truncate">{bar.label}</span>
                          </span>
                        ))}
                      </span>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>

            {/* side panel */}
            {screen.side ? (
              <div className="min-w-0 border-border/70 bg-surface/60 px-4 py-3 lg:border-s">
                <p className="font-display text-xs font-semibold">{t(screen.side.title)}</p>
                <dl className="mt-3 space-y-2">
                  {screen.side.lines.map((line) => (
                    <div key={line.label} className="flex items-baseline justify-between gap-3">
                      <dt className="min-w-0 text-[0.7rem] leading-snug hyphens-auto break-words text-muted-foreground [overflow-wrap:anywhere]">
                        {t(line.label)}
                      </dt>
                      <dd className="shrink-0 text-end text-xs font-semibold text-foreground">
                        {line.value}
                      </dd>
                    </div>
                  ))}
                </dl>
                <span className="mt-4 block rounded-md border border-primary/40 bg-primary/10 px-3 py-2 text-center text-[0.7rem] font-semibold text-primary">
                  {t("Open details")}
                </span>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {caption ? (
        <figcaption className="mx-auto mt-5 flex max-w-xl flex-col items-center">
          <span className="flex w-full items-center gap-3" aria-hidden>
            <span className="h-px flex-1 bg-border" />
            <span className="flex items-center gap-2 font-mono text-[0.62rem] leading-none font-semibold tracking-[0.16em] text-muted-foreground uppercase">
              <span className="size-1.5 bg-primary" />
              {t("How it works")}
            </span>
            <span className="h-px flex-1 bg-border" />
          </span>
          <span className="mt-3 max-w-lg text-center text-sm leading-relaxed font-medium text-foreground/75">
            {t(screen.caption)}
          </span>
        </figcaption>
      ) : null}
    </figure>
  );
}
